import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import HistoricalImportShellBanner from './HistoricalImportShellBanner';
import useHistoricalImportBanner from '../../hooks/useHistoricalImportBanner';
import { useAuth } from '../../AuthContext';
import { useInitialImportStatus } from '../../contexts/InitialImportStatusContext';
import { useToast } from '../../contexts/ToastContext';
import { useOptionalAiConsent } from '../../contexts/AiConsentContext';
import {
  isInitialImportBannerSuppressed,
  subscribeInitialImportBannerSuppression,
} from '../../utils/importBannerSuppression';

const SUCCESS_DWELL_MS = 4200;
const HISTORICAL_IMPORT_TOAST_ID = 'historical-import-status';
const ROUTES_WITH_INLINE_IMPORT_STATE = [
  '/onboarding',
  '/dashboard',
  '/transactions',
  '/activity',
  '/budgets',
  '/accounts',
  '/categories',
  '/household',
  '/income',
  '/net-worth',
  '/insights',
  '/settings',
  '/m',
];

const GlobalInitialImportBanner = () => {
  const location = useLocation();
  const { household } = useAuth();
  const { setPinnedToast, clearPinnedToast } = useToast();
  const householdId = household?.id;
  const { bankStatus, experience } = useInitialImportStatus();
  const aiConsent = useOptionalAiConsent();
  const aiCategorizationEnabled = aiConsent?.granted === true
    && aiConsent?.state?.processingEnabled === true;
  const [bannerSuppressed, setBannerSuppressed] = useState(() => isInitialImportBannerSuppressed());
  const pathname = String(location?.pathname || '');
  const routeSuppressed = ROUTES_WITH_INLINE_IMPORT_STATE.some((route) => pathname.startsWith(route));
  const {
    historicalImportBannerPhase,
    acknowledgeHistoricalImportSuccess,
  } = useHistoricalImportBanner({
    householdId,
    bankStatus,
  });

  useEffect(() => subscribeInitialImportBannerSuppression(setBannerSuppressed), []);

  const toastNode = useMemo(() => {
    if (!historicalImportBannerPhase || bannerSuppressed || routeSuppressed) return null;
    return (
      <HistoricalImportShellBanner
        phase={historicalImportBannerPhase}
        aiCategorizationEnabled={aiCategorizationEnabled}
        recentEvents={experience.recentEvents}
        experienceContext={bankStatus?.onboardingImportStatus?.experienceContext || 'onboarding'}
        importedTransactionCount={bankStatus?.currentImportTransactionCount}
        activeInstitutionName={
          bankStatus?.onboardingImportStatus?.activeInstitutionName
          || bankStatus?.onboardingImportStatus?.importInstitutionName
          || bankStatus?.onboardingImportStatus?.connectedInstitutionName
          || bankStatus?.activeInstitutionName
          || null
        }
        queuedImportCount={
          bankStatus?.onboardingImportStatus?.queuedImportCount
          ?? bankStatus?.onboardingImportStatus?.queuedImportsCount
          ?? bankStatus?.onboardingImportStatus?.queuedInstitutionCount
          ?? bankStatus?.onboardingImportStatus?.queuedInstitutionsCount
          ?? bankStatus?.queuedImportCount
          ?? 0
        }
      />
    );
  }, [
    bankStatus?.activeInstitutionName,
    bankStatus?.onboardingImportStatus?.experienceContext,
    bankStatus?.onboardingImportStatus?.activeInstitutionName,
    bankStatus?.onboardingImportStatus?.importInstitutionName,
    bankStatus?.onboardingImportStatus?.connectedInstitutionName,
    bankStatus?.onboardingImportStatus?.queuedImportCount,
    bankStatus?.onboardingImportStatus?.queuedImportsCount,
    bankStatus?.onboardingImportStatus?.queuedInstitutionCount,
    bankStatus?.onboardingImportStatus?.queuedInstitutionsCount,
    bankStatus?.queuedImportCount,
    bankStatus?.currentImportTransactionCount,
    aiCategorizationEnabled,
    bannerSuppressed,
    experience.recentEvents,
    historicalImportBannerPhase,
    routeSuppressed,
  ]);

  useEffect(() => {
    if (historicalImportBannerPhase === 'success' && routeSuppressed) {
      acknowledgeHistoricalImportSuccess();
    }
  }, [acknowledgeHistoricalImportSuccess, historicalImportBannerPhase, routeSuppressed]);

  useEffect(() => {
    if (historicalImportBannerPhase !== 'success' || bannerSuppressed || routeSuppressed) return undefined;
    const timer = window.setTimeout(() => {
      acknowledgeHistoricalImportSuccess();
    }, SUCCESS_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [acknowledgeHistoricalImportSuccess, bannerSuppressed, historicalImportBannerPhase, routeSuppressed]);

  useEffect(() => {
    if (!toastNode) {
      clearPinnedToast(HISTORICAL_IMPORT_TOAST_ID);
      return undefined;
    }

    setPinnedToast({
      id: HISTORICAL_IMPORT_TOAST_ID,
      node: toastNode,
    });

    return () => {
      clearPinnedToast(HISTORICAL_IMPORT_TOAST_ID);
    };
  }, [clearPinnedToast, setPinnedToast, toastNode]);

  return null;
};

export default GlobalInitialImportBanner;
